/**
 * Status auras — what is currently true about a body, drawn on the body.
 *
 * ── The gap this closes ───────────────────────────────────────────────────
 *
 * `PlayerSnapshot.effects` has carried every active effect by name since the
 * wire was written, and its comment says "for client VFX". The client drew
 * exactly one of them: `invisible`. Everything else — speed, slow, stun,
 * damage reduction, a damage-over-time ticking on you — was invisible state.
 *
 * That is a fight-losing amount of missing information in a PvP game. Overclock
 * grants 2 seconds of +40% move speed and was announced by a 420ms burst at the
 * moment of cast: for the remaining 1.6s the buff was live and nothing on
 * screen said so, to you OR to the person deciding whether to chase you. The
 * same held for a stun you were walking into and a Walled Garden you were
 * hitting.
 *
 * ── Why a ground ring, and why exactly one ────────────────────────────────
 *
 * On the ground, because that is where the fixed camera makes distances honest
 * and where the aim reticle and every telegraph already live — a status that
 * floated at chest height would be the only thing in the scene not obeying that
 * rule.
 *
 * Exactly one, because a body can legitimately carry four effects at once and
 * four concentric rings is not information, it is a target. So effects are
 * ranked and the most urgent wins. The ranking is deliberately "what would kill
 * me first": being stunned outranks being slowed, which outranks being buffed,
 * because if you are stunned nothing else about you matters this second.
 *
 * Effects that are pure sim bookkeeping — `busy`, `iframe`, `dash`,
 * `slow_immune` — are NOT drawn. They change what the sim allows, not what a
 * player should react to, and `busy` in particular is true during the recovery
 * of every single cast, so drawing it would mean a ring that is on almost
 * always and therefore says nothing.
 *
 * Node materials, because the renderer is WebGPU: a legacy material here throws
 * `Uniform "null" not declared` on the first frame.
 */

import * as THREE from 'three/webgpu';
import { PLAYER_HALF_HEIGHT } from '@chaos/sim';
import { DECAL_LIFT } from './decals.js';

/**
 * Most urgent first. The first match in this list is the one that gets drawn.
 *
 * `color: null` means "use the character's own colour" — a buff should read as
 * that player getting stronger, and tinting it with their identity colour says
 * so without needing a legend. Debuffs get fixed colours instead, because a
 * debuff is information about a threat and must mean the same thing whoever is
 * wearing it.
 */
const RANKED = [
  { effect: 'stun', color: 0xffc107, pulse: 7.5, scale: 1.15 },
  { effect: 'damage_over_time', color: 0xff3d3d, pulse: 5.0, scale: 1.05 },
  { effect: 'slow', color: 0x8a6bff, pulse: 2.2, scale: 1.0 },
  { effect: 'damage_reduction', color: 0xffffff, pulse: 1.6, scale: 1.25 },
  { effect: 'speed', color: null, pulse: 4.0, scale: 1.3 },
];

/** Just outside the body so the ring reads as an aura, not a shadow. */
const BASE_RADIUS = 0.95;
/** Where the body's origin sits above its feet — a capsule fact, not a taste. */
const FOOT_OFFSET = -PLAYER_HALF_HEIGHT;

let _geo = null;

function ringGeometry() {
  if (!_geo) {
    _geo = new THREE.RingGeometry(BASE_RADIUS * 0.82, BASE_RADIUS, 40);
    _geo.rotateX(-Math.PI / 2);
  }
  return _geo;
}

/**
 * Sets (or clears) the aura on one character group.
 *
 * Mirrors `setCharacterShimmer`: it takes the group rather than a player id, so
 * the local player and a remote one go through the identical call from their
 * own render paths and cannot drift apart.
 *
 * @param {THREE.Object3D} group the character model group
 * @param {string[]} effects `PlayerSnapshot.effects`
 * @param {number} colorHex the character's own colour, for buffs
 */
export function setStatusAura(group, effects, colorHex) {
  const active = effects?.length ? RANKED.find((r) => effects.includes(r.effect)) : null;

  if (!active) {
    if (group.userData.auraMesh) group.userData.auraMesh.visible = false;
    return;
  }

  let mesh = group.userData.auraMesh;
  if (!mesh) {
    const mat = new THREE.MeshBasicNodeMaterial({ color: 0xffffff });
    mat.transparent = true;
    mat.depthWrite = false;
    mesh = new THREE.Mesh(ringGeometry(), mat);
    // The group is rotated to face the aim; the ring must not spin with it, and
    // a frustum test on a child this small under a moving parent pops.
    mesh.frustumCulled = false;
    mesh.renderOrder = 2;
    group.add(mesh);
    group.userData.auraMesh = mesh;
  }

  mesh.visible = true;
  mesh.position.set(0, FOOT_OFFSET + DECAL_LIFT, 0);
  // Undo the parent's yaw so the ring stays put while the body turns. A ring is
  // radially symmetric so this is invisible — until a future ring is not, and
  // then this is why it still looks right.
  mesh.rotation.y = -group.rotation.y;
  mesh.scale.setScalar(active.scale);
  mesh.material.color.setHex(active.color ?? colorHex);

  // Breathing, not blinking. A hard on/off at 7Hz is a seizure risk and reads
  // as a rendering fault; a sine between 0.35 and 0.75 reads as "live".
  const t = performance.now() / 1000;
  mesh.material.opacity = 0.55 + 0.2 * Math.sin(t * active.pulse);
}

/** Drops the aura's own material. The geometry is shared and stays. */
export function disposeStatusAura(group) {
  const mesh = group.userData?.auraMesh;
  if (!mesh) return;
  group.remove(mesh);
  mesh.material?.dispose?.();
  group.userData.auraMesh = null;
}
